import {useCallback} from 'react'
import {ObjUtil, Time} from 'yougile-util'
import {
  ChatMessage,
  DataObjectId,
  DataObjectReactions,
  Hub,
  Reaction
} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {
  ChatMessageReactedUser,
  ChatMessageReaction
} from '@components/common/base/chat-message'
import {useForceUpdate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'

export const useReactions = (
  reactions?: DataObjectReactions
): ChatMessageReaction[] => {
  const map = new Map<string, ChatMessageReactedUser[]>()

  ObjUtil.forEach(reactions, (userId: DataObjectId, reactions: Reaction[]) => {
    for (const reaction of reactions) {
      if (!reaction) {
        continue
      }

      const reactedUsers = reaction.smiley ? map.get(reaction.smiley) || [] : []
      reactedUsers.push({userId, timestamp: reaction.timestamp})
      map.set(reaction.smiley, reactedUsers)
    }
  })

  return Array.from(map.entries())
    .map(([smiley, reactedUsers]) => ({smiley, reactedUsers}))
    .filter(x => x.reactedUsers.length > 0)
    .sort((a, b) => b.reactedUsers.length - a.reactedUsers.length)
}

interface UseEmojiReactionsOptions {
  message: ChatMessage
  hub?: Hub
}

interface UseEmojiReactions {
  reactions: ChatMessageReaction[]
  onReactionSelect: (reaction: string) => void
}

export const useEmojiReactions = ({
  message,
  hub
}: UseEmojiReactionsOptions): UseEmojiReactions => {
  const forceUpdate = useForceUpdate()
  const reactions = useReactions(message.reactions)

  const hubCommit = useCallback((): void => {
    hub?.commit()
    forceUpdate()
    void Tracking.send(TrackingEvent.reactionChange)
  }, [hub, forceUpdate])

  const addReaction = useCallback(
    (message: ChatMessage, smiley: Reaction['smiley']): void => {
      const reaction: Reaction = {
        timestamp: Time.now(),
        smiley
      }
      message.reactions = {
        ...message.reactions,
        [globals.currentUserId]: message.reactions?.[globals.currentUserId]
          ? [...message.reactions[globals.currentUserId], reaction]
          : [reaction]
      }
      hubCommit()
    },
    [hubCommit]
  )

  const removeReaction = useCallback(
    (message: ChatMessage, smiley: Reaction['smiley']): void => {
      const reactions: DataObjectReactions = {
        ...message.reactions,
        [globals.currentUserId]: message.reactions?.[
          globals.currentUserId
        ].filter(reaction => reaction.smiley !== smiley)
      }
      const filteredReactions = ObjUtil.filter(
        reactions,
        (_, xs) => xs.length > 0
      )
      if (!ObjUtil.isEmpty(filteredReactions)) {
        message.reactions = filteredReactions as DataObjectReactions
      } else {
        delete message.reactions
      }
      hubCommit()
    },
    [hubCommit]
  )

  const onSelect = useCallback(
    (smiley: Reaction['smiley']): void => {
      // Hub may change and `message` to `hub` link may be broken, so we reacquire the message
      const msg = hub?.chat?.messages.get(message.timestamp)
      const userId = globals.currentUserId
      if (msg) {
        if (msg.reactions?.[userId]?.some(x => x?.smiley === smiley)) {
          removeReaction(msg, smiley)
        } else {
          addReaction(msg, smiley)
        }
      }
    },
    [addReaction, removeReaction, hub, message.timestamp]
  )

  return {reactions, onReactionSelect: onSelect}
}
